import './style.css';
import { initAuthUI, startTokenRefresh } from './auth';

// Tab functionality
document.addEventListener('DOMContentLoaded', () => {
    initAuthUI();
    startTokenRefresh();
    const tabButtons = document.querySelectorAll<HTMLButtonElement>('.tab-btn');
    const tabContents = document.querySelectorAll<HTMLDivElement>('.tab-content');

    function activateTab(tabName: string) {
        tabButtons.forEach(btn => btn.classList.remove('active'));
        tabContents.forEach(content => content.classList.remove('active'));

        const targetBtn = document.querySelector<HTMLButtonElement>(`.tab-btn[data-tab="${tabName}"]`);
        const targetContent = document.getElementById(tabName);
        if (targetBtn) targetBtn.classList.add('active');
        if (targetContent) targetContent.classList.add('active');
    }

    tabButtons.forEach(button => {
        button.addEventListener('click', () => {
            const targetTab = button.getAttribute('data-tab');
            if (!targetTab) return;
            activateTab(targetTab);
            history.replaceState(null, '', `${window.location.pathname}#${targetTab}`);
        });
    });

    // Activate tab from URL hash (e.g. /offerings#delivery)
    const hash = window.location.hash.replace('#', '');
    if (hash && document.getElementById(hash)) {
        activateTab(hash);
    }

    // Listen for hash changes (e.g. clicking nav links while on the same page)
    window.addEventListener('hashchange', () => {
        const newHash = window.location.hash.replace('#', '');
        if (newHash && document.getElementById(newHash)) {
            activateTab(newHash);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    });

    // Mobile menu toggle
    const mobileMenuBtn = document.getElementById('mobileMenuBtn');
    const navCenter = document.querySelector<HTMLDivElement>('.nav-center');
    const navLinks = document.querySelector<HTMLDivElement>('.nav-links');
    const mobileNav = navCenter || navLinks;

    if (mobileMenuBtn && mobileNav) {
        mobileMenuBtn.addEventListener('click', () => {
            mobileNav.classList.toggle('active');
            mobileMenuBtn.classList.toggle('active');
        });
    }

    // Nav search — redirect to offerings with query
    const navSearchForm = document.getElementById('navSearchForm') as HTMLFormElement;
    if (navSearchForm) {
        navSearchForm.addEventListener('submit', (e) => {
            e.preventDefault();
            const input = navSearchForm.querySelector('.nav-search-input') as HTMLInputElement;
            const q = input?.value.trim();
            if (q) {
                window.location.href = `/offerings#giftcards?q=${encodeURIComponent(q)}`;
            }
        });
    }

    // Smooth scrolling for navigation links
    document.querySelectorAll<HTMLAnchorElement>('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', function(e) {
            e.preventDefault();
            const target = document.querySelector<HTMLElement>(this.getAttribute('href') || '');
            if (target) {
                target.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        });
    });

    // Add scroll effect to navbar
    const navbar = document.querySelector<HTMLElement>('.navbar');

    window.addEventListener('scroll', () => {
        const currentScroll = window.pageYOffset;

        if (navbar) {
            if (currentScroll > 100) {
                navbar.style.boxShadow = 'var(--shadow-lg)';
            } else {
                navbar.style.boxShadow = 'var(--shadow-sm)';
            }
        }
    });

    // Animate cards on scroll
    const observerOptions: IntersectionObserverInit = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            const target = entry.target as HTMLElement;
            if (entry.isIntersecting) {
                target.style.opacity = '1';
                target.style.transform = 'translateY(0)';
            }
        });
    }, observerOptions);

    document.querySelectorAll<HTMLElement>('.offering-card').forEach(card => {
        card.style.opacity = '0';
        card.style.transform = 'translateY(30px)';
        card.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
        observer.observe(card);
    });

    // ── Savings Calculator ──
    const savingsSlider = document.getElementById('savingsSlider') as HTMLInputElement;
    const fullPriceEl = document.getElementById('fullPriceAmount');
    const jkPriceEl = document.getElementById('jkPriceAmount');
    const savingsEl = document.getElementById('savingsAmount');

    const sliderValueEl = document.getElementById('savingsSliderValue');

    function updateSavings() {
        if (!savingsSlider || !fullPriceEl || !jkPriceEl || !savingsEl) return;
        const orders = parseInt(savingsSlider.value);
        const avgOrderPrice = 30;
        const discountRate = 0.5;
        const fullPrice = orders * avgOrderPrice;
        const jkPrice = Math.round(fullPrice * (1 - discountRate));
        const savings = fullPrice - jkPrice;
        fullPriceEl.textContent = `$${fullPrice}`;
        jkPriceEl.textContent = `$${jkPrice}`;
        savingsEl.textContent = `$${savings}`;

        // Update slider track fill
        const pct = ((orders - 1) / (30 - 1)) * 100;
        savingsSlider.style.background = `linear-gradient(90deg, #C9A96E 0%, #D4B97A ${pct}%, #3D3632 ${pct}%, #3D3632 100%)`;

        // Update slider value bubble position
        if (sliderValueEl) {
            sliderValueEl.textContent = String(orders);
            const thumbOffset = 11; // half of 22px thumb
            const trackWidth = savingsSlider.offsetWidth - thumbOffset * 2;
            const left = thumbOffset + (pct / 100) * trackWidth;
            sliderValueEl.style.left = `${left}px`;
        }
    }

    if (savingsSlider) {
        savingsSlider.addEventListener('input', updateSavings);
        updateSavings();
    }

    // ── Subtle Floating Orbs ──
    const particlesContainer = document.getElementById('heroParticles');
    if (particlesContainer) {
        for (let i = 0; i < 12; i++) {
            const orb = document.createElement('span');
            orb.className = 'hero-particle';
            const size = 3 + Math.random() * 5;
            orb.style.width = `${size}px`;
            orb.style.height = `${size}px`;
            orb.style.left = `${Math.random() * 100}%`;
            orb.style.animationDuration = `${15 + Math.random() * 20}s`;
            orb.style.animationDelay = `${Math.random() * 10}s`;
            orb.style.opacity = `${0.15 + Math.random() * 0.25}`;
            particlesContainer.appendChild(orb);
        }
    }

    // ── Animated Stat Counters ──
    const statNumbers = document.querySelectorAll<HTMLElement>('.hero-stat-number[data-count]');
    let statsCounted = false;

    function animateCounters() {
        if (statsCounted) return;
        statsCounted = true;

        statNumbers.forEach(el => {
            const target = parseInt(el.dataset.count || '0');
            const suffix = el.dataset.suffix || '';
            const format = el.dataset.format;
            const duration = 2200;
            let startTime: number | null = null;

            function tick(now: number) {
                if (!startTime) startTime = now;
                const elapsed = now - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const eased = 1 - Math.pow(1 - progress, 4);
                const current = Math.round(eased * target);

                if (format === 'k') {
                    if (current >= 1000) {
                        const k = current / 1000;
                        el.textContent = `${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}K${suffix}`;
                    } else {
                        el.textContent = `${current}${suffix}`;
                    }
                } else {
                    el.textContent = `${current}${suffix}`;
                }

                if (progress < 1) requestAnimationFrame(tick);
            }
            requestAnimationFrame(tick);
        });
    }

    const statsBox = document.querySelector<HTMLElement>('.hero-stats');
    if (statsBox) {
        const statsObserver = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) animateCounters();
        }, { threshold: 0.5 });
        statsObserver.observe(statsBox);
    }

    // ── Scroll Reveal for Step Cards & Features ──
    const revealElements = document.querySelectorAll<HTMLElement>('.step-card, .feature-item, .cta-box, .features-section, .testimonial-card, .testimonials-section, .savings-calculator');
    revealElements.forEach((el, i) => {
        el.classList.add('reveal');
        const delayClass = `reveal-delay-${(i % 3) + 1}`;
        el.classList.add(delayClass);
    });

    const revealObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('visible');
            }
        });
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });

    document.querySelectorAll<HTMLElement>('.reveal').forEach(el => revealObserver.observe(el));
});
